import { useCallback, useEffect, useRef, useState } from "react"

import { Button, Col, Modal, Row, Spinner } from "react-bootstrap"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import { finalize } from "rxjs/operators"

import FFClinicGroupRow from "./FFClinicGroupRow"
import { GetFFClinicGroupsObservable, SetExcludedClinicGroupsObservable } from "../../../../api/Clinics/ClinicGroupsGet"
import ClinicGroup from "../../../../api/Clinics/Models/ClinicGroup"
import ClinicGroupFF from "../../../../api/Clinics/Models/ClinicGroupFF"
import FormatError from "../../../../formatters/FormatError"
import ActionButton from "../../../ActionButton"

interface IClinicGroupsFFModalProps {
  excludedGroups: ClinicGroup[]
  onHide?: () => void
}

function ClinicGroupsFFModal(props: IClinicGroupsFFModalProps) {
  const { onHide, excludedGroups } = props
  const [show, setShow] = useState(false)
  const [groups, setGroups] = useState<ClinicGroupFF[]>([])
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [savingGroups, setSavingGroups] = useState(false)

  const subsRef = useRef({
    subs: new Subscription(),
  })

  useEffect(() => {
    const { subs } = subsRef.current
    return () => subs.unsubscribe()
  }, [])

  useEffect(() => {
    if (show) {
      setLoadingGroups(true)
      subsRef.current.subs.add(
        GetFFClinicGroupsObservable()
          .pipe(finalize(() => setLoadingGroups(false)))
          .subscribe(
            (data) => {
              setGroups(
                data.map<ClinicGroupFF>((group) => ({
                  ...group,
                  SearchRestrictID: excludedGroups.find((e) => e.RestrictCode === group.Id.toString())
                    ?.SearchRestrictId,
                  excluded: excludedGroups.some((e) => e.RestrictCode === group.Id.toString()),
                }))
              )
            },
            (error) => toast.error(FormatError(error))
          )
      )
    }
  }, [excludedGroups, show])

  const handleHide = () => {
    onHide?.()
    setShow(false)
  }

  const handleSave = () => {
    setSavingGroups(true)
    SetExcludedClinicGroupsObservable(groups)
      .pipe(finalize(() => setSavingGroups(false)))
      .subscribe(
        (data) => {
          handleHide()
          toast.success(data)
        },
        (error) => toast.error(FormatError(error))
      )
  }

  const handleExcludeGroup = useCallback(
    (group: ClinicGroupFF, excluded: boolean) => {
      setGroups(groups.map((p) => (p.Id === group.Id && p.excluded !== excluded ? { ...p, excluded } : p)))
    },
    [groups]
  )

  return (
    <>
      <Button variant="primary" className="mr-1" disabled={loadingGroups} onClick={() => setShow(true)}>
        Group Search
      </Button>
      {show && (
        <Modal show onHide={handleHide} backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>Clinic Group Name</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="font-weight-bold mb-1">
              <Col sm={9}>Name</Col>
              <Col sm={3} className="text-center">
                Excluded
              </Col>
            </Row>
            {loadingGroups ? (
              <Spinner animation="border" variant="secondary" className="mb-1" />
            ) : (
              groups.map((group) => <FFClinicGroupRow key={group.Id} group={group} onClick={handleExcludeGroup} />)
            )}
          </Modal.Body>
          <Modal.Footer>
            <ActionButton
              variant="primary"
              className="mr-1"
              spin={savingGroups}
              disableOnSpin
              disabled={loadingGroups || groups.length === 0}
              onClick={handleSave}
            >
              Save
            </ActionButton>
            <Button variant="secondary" className="mr-1" disabled={loadingGroups} onClick={() => setShow(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}

ClinicGroupsFFModal.defaultProps = {
  onHide: undefined,
}

export default ClinicGroupsFFModal
